import { useSession } from "hooks/useSession";
import { useState, useEffect, useCallback, useRef } from "react";
import { fetchWrapper } from "utils/apiFetch";

// ─────────────────────────────────────────────────────────────────────────────
// Config — adjust to your Django URL
// ─────────────────────────────────────────────────────────────────────────────

const API_URL = "https://api.mokshiq.in/api/api-integration/";

// Simulated logged-in user role — replace with your auth context
// Roles: "White label" | "Admin" | "Main Admin"
const CURRENT_ROLE = "White label";  // swap from your auth context

// ─────────────────────────────────────────────────────────────────────────────
// API helper
// ─────────────────────────────────────────────────────────────────────────────

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
}

async function apiPost(params, file = null) {
  const fd = new FormData();
  Object.entries(params).forEach(([k, v]) => fd.append(k, v));
  if (file) fd.append("document", file);

  const res = await fetchWrapper(API_URL, {
    method: "POST",
   
    body: fd,
  });
  
  return res;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const STATE_CONFIG = {
  pending:  { label: "Pending",  bg: "#FAEEDA", color: "#854F0B", dot: "#BA7517" },
  approved: { label: "Approved", bg: "#E1F5EE", color: "#085041", dot: "#0F6E56" },
  rejected: { label: "Rejected", bg: "#FCEBEB", color: "#791F1F", dot: "#A32D2D" },
};

const ROLE_CONFIG = {
  "White label": { label: "White Label",  bg: "#EEEDFE", color: "#3C3489" },
  "Admin":       { label: "Admin",        bg: "#E6F1FB", color: "#0C447C" },
  "Main Admin":  { label: "Main Admin",   bg: "#E1F5EE", color: "#085041" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Small UI components
// ─────────────────────────────────────────────────────────────────────────────

function StateBadge({ state }) {
  const cfg = STATE_CONFIG[state] || STATE_CONFIG.pending;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: "3px 10px", borderRadius: 6,
      fontSize: 12, fontWeight: 500, whiteSpace: "nowrap",
      display: "inline-flex", alignItems: "center", gap: 5,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.dot, display: "inline-block" }} />
      {cfg.label}
    </span>
  );
}

function RoleBadge({ role }) {
  const cfg = ROLE_CONFIG[role] || ROLE_CONFIG["Admin"];
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      padding: "2px 8px", borderRadius: 6,
      fontSize: 11, fontWeight: 500, whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background: "#fff", border: "0.5px solid #e5e7eb",
      borderRadius: 12, padding: "1rem 1.25rem", ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ icon, title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <span style={{ fontSize: 16, color: "#6b7280" }}>{icon}</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{title}</span>
      </div>
      {right}
    </div>
  );
}

function Spinner({ size = 28 }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 0" }}>
      <div style={{
        width: size, height: size, border: "3px solid #e5e7eb",
        borderTopColor: "#185FA5", borderRadius: "50%",
        animation: "spin 0.7s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  const c = type === "success"
    ? { bg: "#E1F5EE", border: "#9FE1CB", text: "#085041" }
    : { bg: "#FCEBEB", border: "#F7C1C1", text: "#791F1F" };
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 3000,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      borderRadius: 10, padding: "10px 18px", fontSize: 13, fontWeight: 500,
      boxShadow: "0 4px 20px rgba(0,0,0,0.1)", display: "flex", alignItems: "center", gap: 10,
      maxWidth: 360,
    }}>
      {type === "success" ? "✅" : "❌"} {message}
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: c.text, lineHeight: 1, marginLeft: 4 }}>×</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Submit Modal (White Label / Admin)
// ─────────────────────────────────────────────────────────────────────────────

function SubmitModal({ onClose, onSubmitted }) {
  const [remark,   setRemark]   = useState("");
  const [docFile,  setDocFile]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [errors,   setErrors]   = useState({});
  const fileRef = useRef();

  const validate = () => {
    const e = {};
    if (!remark.trim()) e.remark = "Remark is required.";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const r = await apiPost({ action: "SUBMIT", remark }, docFile);
      if (r.status) {
        onSubmitted(r.data, r.reference_num);
        onClose();
      } else {
        setErrors({ submit: r.data });
      }
    } catch {
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1rem",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#fff", borderRadius: 14, width: "100%", maxWidth: 520,
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "0.5px solid #e5e7eb" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: "#111827" }}>🔌 New API Integration Request</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Processing time: approx. 2 business days</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#9ca3af" }}>×</button>
        </div>

        {/* Info banner */}
        <div style={{ background: "#E6F1FB", borderBottom: "0.5px solid #B5D4F4", padding: "10px 1.25rem", display: "flex", alignItems: "flex-start", gap: 8 }}>
          <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
          <span style={{ fontSize: 12, color: "#0C447C", lineHeight: 1.6 }}>
            Submit your API integration details along with supporting documentation.
            Once submitted, the request will be reviewed and you will be notified upon approval.
          </span>
        </div>

        {/* Body */}
        <div style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>

          {/* Remark */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Remark / Description <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <textarea
              value={remark}
              onChange={e => { setRemark(e.target.value); setErrors(er => ({ ...er, remark: null })); }}
              rows={4}
              placeholder="Describe the API integration you require — include API name, purpose, and any technical details..."
              style={{
                padding: "10px 12px",
                border: `1px solid ${errors.remark ? "#ef4444" : "#d1d5db"}`,
                borderRadius: 8, fontSize: 13, outline: "none",
                resize: "vertical", fontFamily: "inherit", color: "#111827",
                lineHeight: 1.6,
              }}
            />
            {errors.remark && <span style={{ fontSize: 11, color: "#ef4444" }}>{errors.remark}</span>}
          </div>

          {/* Document upload */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Supporting Document <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>(optional — PDF, DOC, image)</span>
            </label>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={e => setDocFile(e.target.files[0] || null)}
              style={{ display: "none" }} />
            <div
              onClick={() => fileRef.current.click()}
              style={{
                border: `1.5px dashed ${docFile ? "#9FE1CB" : "#d1d5db"}`,
                borderRadius: 8, padding: "16px 12px",
                textAlign: "center", cursor: "pointer",
                background: docFile ? "#E1F5EE" : "#f9fafb",
                transition: "all 0.15s",
              }}
            >
              {docFile ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span style={{ fontSize: 20 }}>📎</span>
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#085041" }}>{docFile.name}</div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>{(docFile.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); setDocFile(null); fileRef.current.value = ""; }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#9ca3af", marginLeft: 8 }}>
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 24, marginBottom: 6 }}>📁</div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>Click to upload document</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>PDF, DOC, JPG, PNG — max 10 MB</div>
                </>
              )}
            </div>
          </div>

          {errors.submit && (
            <div style={{ background: "#FCEBEB", border: "0.5px solid #F7C1C1", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#791F1F" }}>
              ❌ {errors.submit}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "0.875rem 1.25rem", borderTop: "0.5px solid #e5e7eb", background: "#f9fafb" }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: "7px 18px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#374151" }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: "7px 18px", border: "none", borderRadius: 8, background: saving ? "#93c5fd" : "#185FA5", color: "#fff", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", fontWeight: 500 }}>
            {saving ? "Submitting…" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Detail / Approve Modal
// ─────────────────────────────────────────────────────────────────────────────

function DetailModal({ request, role, onClose, onAction }) {
  const [rejecting,      setRejecting]      = useState(false);
  const [rejectionNote,  setRejectionNote]  = useState("");
  const [actioning,      setActioning]      = useState(false);

  const handleApprove = async () => {
    setActioning(true);
    try {
      const r = await apiPost({ action: "APPROVE", id: request.id });
      onAction(r.data, r.status ? "success" : "error");
      if (r.status) onClose();
    } catch {
      onAction("Something went wrong.", "error");
    } finally {
      setActioning(false);
    }
  };

  const handleReject = async () => {
    setActioning(true);
    try {
      const r = await apiPost({ action: "REJECT", id: request.id, remark: rejectionNote });
      onAction(r.data, r.status ? "success" : "error");
      if (r.status) onClose();
    } catch {
      onAction("Something went wrong.", "error");
    } finally {
      setActioning(false);
    }
  };

  const isPending   = request.approval_state === "pending";
  const isMainAdmin = role === "Main Admin";

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1rem",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: "#fff", borderRadius: 14, width: "100%", maxWidth: 540,
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.25rem", borderBottom: "0.5px solid #e5e7eb" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, color: "#111827", marginBottom: 4 }}>
              API Integration Request
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, background: "#f3f4f6", color: "#6b7280", padding: "2px 8px", borderRadius: 6 }}>
                Ref: {request.reference_num}
              </span>
              <StateBadge state={request.approval_state} />
              <RoleBadge role={request.submitted_role} />
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "#9ca3af" }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "1rem" }}>

          {/* Submitted by */}
          <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 12px", display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>👤</span>
            <div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>Submitted by</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{request.submitted_by}</div>
            </div>
          </div>

          {/* Remark */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Remark / Description</div>
            <div style={{
              background: "#f9fafb", border: "0.5px solid #e5e7eb",
              borderRadius: 8, padding: "12px 14px",
              fontSize: 13, color: "#374151", lineHeight: 1.7,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}>
              {request.remark || <span style={{ color: "#9ca3af" }}>No remark provided.</span>}
            </div>
          </div>

          {/* Document */}
          {request.document && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Supporting Document</div>
              <a
                href={`https://api.mokshiq.in/${request.document}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "8px 14px", border: "1px solid #B5D4F4",
                  borderRadius: 8, background: "#E6F1FB",
                  color: "#0C447C", fontSize: 13, fontWeight: 500,
                  textDecoration: "none",
                }}
              >
                📎 View Document
                <span style={{ fontSize: 11, color: "#378ADD" }}>↗</span>
              </a>
            </div>
          )}

          {/* 2-day notice */}
          <div style={{ background: "#FAEEDA", border: "0.5px solid #FAC775", borderRadius: 8, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start" }}>
            <span style={{ fontSize: 15, flexShrink: 0 }}>⏱</span>
            <span style={{ fontSize: 12, color: "#633806", lineHeight: 1.6 }}>
              API integration requests typically take <strong>2 business days</strong> to process.
              The submitter will be notified once the request is reviewed.
            </span>
          </div>

          {/* Rejection note input (only shown when Main Admin clicks Reject) */}
          {isMainAdmin && isPending && rejecting && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Rejection Note <span style={{ fontWeight: 400, color: "#9ca3af" }}>(optional)</span></label>
              <textarea
                value={rejectionNote}
                onChange={e => setRejectionNote(e.target.value)}
                rows={3}
                placeholder="Provide a reason for rejection..."
                style={{
                  padding: "8px 12px", border: "1px solid #fca5a5",
                  borderRadius: 8, fontSize: 13, outline: "none",
                  resize: "vertical", fontFamily: "inherit", color: "#111827",
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "0.875rem 1.25rem", borderTop: "0.5px solid #e5e7eb", background: "#f9fafb" }}>
          <button onClick={onClose}
            style={{ padding: "7px 18px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#374151" }}>
            Close
          </button>

          {isMainAdmin && isPending && (
            <div style={{ display: "flex", gap: 8 }}>
              {rejecting ? (
                <>
                  <button onClick={() => setRejecting(false)} disabled={actioning}
                    style={{ padding: "7px 16px", border: "1px solid #d1d5db", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#374151" }}>
                    Cancel
                  </button>
                  <button onClick={handleReject} disabled={actioning}
                    style={{ padding: "7px 16px", border: "none", borderRadius: 8, background: actioning ? "#f09595" : "#A32D2D", color: "#fff", fontSize: 13, cursor: actioning ? "not-allowed" : "pointer", fontWeight: 500 }}>
                    {actioning ? "Rejecting…" : "Confirm Reject"}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setRejecting(true)}
                    style={{ padding: "7px 16px", border: "1px solid #fca5a5", borderRadius: 8, background: "#fef2f2", color: "#A32D2D", fontSize: 13, cursor: "pointer" }}>
                    ✕ Reject
                  </button>
                  <button onClick={handleApprove} disabled={actioning}
                    style={{ padding: "7px 16px", border: "none", borderRadius: 8, background: actioning ? "#9FE1CB" : "#0F6E56", color: "#fff", fontSize: 13, cursor: actioning ? "not-allowed" : "pointer", fontWeight: 500 }}>
                    {actioning ? "Approving…" : "✓ Approve"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function APIIntegrationPage() {
    const { userid, role } = useSession();
    // replace with your auth context

  const [requests,     setRequests]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [submitOpen,   setSubmitOpen]   = useState(false);
  const [detailItem,   setDetailItem]   = useState(null);
  const [toast,        setToast]        = useState(null);
  const [filterState,  setFilterState]  = useState("all");

  const showToast = useCallback((message, type = "success") => setToast({ message, type }), []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiPost({ action: "GET" });
      if (!r.status) throw new Error(r.data);
      setRequests(r.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // After approval/rejection, refresh the list
  const handleAction = (message, type) => {
    showToast(message, type);
    fetchRequests();
  };

  const canSubmit = ["White label", "Admin"].includes(role);
  const isMainAdmin = role === "Main Admin";

  const filtered = filterState === "all"
    ? requests
    : requests.filter(r => r.approval_state === filterState);

  const counts = {
    all:      requests.length,
    pending:  requests.filter(r => r.approval_state === "pending").length,
    approved: requests.filter(r => r.approval_state === "approved").length,
    rejected: requests.filter(r => r.approval_state === "rejected").length,
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: "#f3f4f6", minHeight: "100vh", padding: "1.25rem", marginTop:"40px"}}>

      {/* Modals */}
      {submitOpen && (
        <SubmitModal
          onClose={() => setSubmitOpen(false)}
          onSubmitted={(msg, ref) => {
            showToast(`${msg} (Ref: ${ref})`, "success");
            fetchRequests();
          }}
        />
      )}
      {detailItem && (
        <DetailModal
          request={detailItem}
          role={role}
          onClose={() => setDetailItem(null)}
          onAction={handleAction}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: "1rem" }}>

        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <button
            style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#6b7280", padding: 0 }}
            onClick={() => window.history.back()}
          >
            ← Dashboard
          </button>
          {canSubmit && (
            <button
              onClick={() => setSubmitOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 16px", border: "none", borderRadius: 8, background: "#185FA5", color: "#fff", fontSize: 13, cursor: "pointer", fontWeight: 500 }}
            >
              🔌 New Integration Request
            </button>
          )}
        </div>

        {/* Page title */}
        <Card>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 4 }}>API Integration Requests</div>
              <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, maxWidth: 540 }}>
                Submit requests for new API integrations. All requests are reviewed by the Main Admin.
                Processing typically takes <strong>2 business days</strong>.
              </div>
            </div>

            {/* Summary stats */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                { label: "Total",    count: counts.all,      bg: "#f3f4f6",  color: "#374151" },
                { label: "Pending",  count: counts.pending,  bg: "#FAEEDA",  color: "#854F0B" },
                { label: "Approved", count: counts.approved, bg: "#E1F5EE",  color: "#085041" },
                { label: "Rejected", count: counts.rejected, bg: "#FCEBEB",  color: "#791F1F" },
              ].map(s => (
                <div key={s.label} style={{
                  background: s.bg, borderRadius: 8, padding: "8px 14px",
                  textAlign: "center", minWidth: 60,
                }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.count}</div>
                  <div style={{ fontSize: 11, color: s.color, opacity: 0.8 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Requests list */}
        <Card>
          <SectionTitle
            icon="📋"
            title="All Requests"
            right={
              <div style={{ display: "flex", gap: 6 }}>
                {["all", "pending", "approved", "rejected"].map(s => (
                  <button key={s} onClick={() => setFilterState(s)}
                    style={{
                      padding: "4px 12px", borderRadius: 6, border: "1px solid",
                      borderColor: filterState === s ? "#185FA5" : "#e5e7eb",
                      background:  filterState === s ? "#E6F1FB" : "#fff",
                      color:       filterState === s ? "#0C447C" : "#6b7280",
                      fontSize: 12, fontWeight: filterState === s ? 600 : 400, cursor: "pointer",
                      textTransform: "capitalize",
                    }}>
                    {s}
                    <span style={{
                      marginLeft: 5,
                      background: filterState === s ? "#185FA5" : "#f3f4f6",
                      color: filterState === s ? "#fff" : "#6b7280",
                      borderRadius: 8, padding: "0 5px", fontSize: 10,
                    }}>
                      {counts[s]}
                    </span>
                  </button>
                ))}
              </div>
            }
          />

          {loading ? (
            <Spinner />
          ) : error ? (
            <div style={{ textAlign: "center", padding: "2rem 0", color: "#A32D2D" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
              <div style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>
              <button onClick={fetchRequests} style={{ padding: "7px 16px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, cursor: "pointer" }}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2.5rem 0", color: "#9ca3af" }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔌</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "#6b7280", marginBottom: 4 }}>
                {filterState === "all" ? "No requests yet" : `No ${filterState} requests`}
              </div>
              {canSubmit && filterState === "all" && (
                <div style={{ fontSize: 13, color: "#9ca3af", marginBottom: 14 }}>Submit your first API integration request to get started.</div>
              )}
              {canSubmit && filterState === "all" && (
                <button onClick={() => setSubmitOpen(true)}
                  style={{ padding: "7px 18px", border: "none", borderRadius: 8, background: "#185FA5", color: "#fff", fontSize: 13, cursor: "pointer", fontWeight: 500 }}>
                  🔌 New Request
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {filtered.map(req => (
                <div key={req.id}
                  onClick={() => setDetailItem(req)}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    padding: "14px 16px", border: "0.5px solid #e5e7eb",
                    borderRadius: 10, cursor: "pointer", background: "#fff",
                    transition: "border-color 0.15s, background 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#B5D4F4"; e.currentTarget.style.background = "#f9fafb"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#fff"; }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                    background: req.approval_state === "approved" ? "#E1F5EE" : req.approval_state === "rejected" ? "#FCEBEB" : "#E6F1FB",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
                  }}>
                    {req.approval_state === "approved" ? "✅" : req.approval_state === "rejected" ? "❌" : "🔌"}
                  </div>

                  {/* Main info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        Ref: {req.reference_num}
                      </span>
                      <StateBadge state={req.approval_state} />
                      {isMainAdmin && <RoleBadge role={req.submitted_role} />}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 420 }}>
                      {req.remark || "—"}
                    </div>
                    {isMainAdmin && (
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>by {req.submitted_by}</div>
                    )}
                  </div>

                  {/* Doc indicator */}
                  {req.document && (
                    <span style={{ fontSize: 11, color: "#6b7280", background: "#f3f4f6", padding: "3px 8px", borderRadius: 6, flexShrink: 0 }}>
                      📎 doc
                    </span>
                  )}

                  {/* CTA */}
                  <div style={{ flexShrink: 0 }}>
                    {isMainAdmin && req.approval_state === "pending" ? (
                      <span style={{ fontSize: 12, fontWeight: 500, color: "#185FA5", background: "#E6F1FB", padding: "4px 10px", borderRadius: 6 }}>
                        Review →
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>View →</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Role notice */}
        {!canSubmit && !isMainAdmin && (
          <Card>
            <div style={{ textAlign: "center", padding: "0.5rem 0", color: "#9ca3af", fontSize: 13 }}>
              API Integration Requests can only be submitted by White Label or Admin accounts.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}